import React, { useMemo, useRef, useState } from 'react';
import { Button, Grid, Typography } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable } from '../../../../../Components';
import { DateField } from '../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import { RichTextEditor } from '../../../../../Components/RichTextEditor/RichTextEditor';
import { OnboardingDraftSelect, OnboardingDraftText, emptyRequirement, useOnboardingDraft, validRequirement } from './OnboardingDraft';
import { useStyles } from './Onboarding.styles';

const toDateOnly = (value?: TypedFormFieldValue<Date>) =>
  value && !Number.isNaN(value.value.getTime())
    ? `${value.value.getFullYear()}-${String(value.value.getMonth() + 1).padStart(2, '0')}-${String(value.value.getDate()).padStart(2, '0')}`
    : undefined;
const fromDateOnly = (value?: string) => {
  if (!value) return undefined;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : { value: date };
};
const datePickerPopoverProps = {
  transitionDuration: 0,
  PaperProps: { style: { visibility: 'hidden' as const } },
  TransitionProps: {
    onEntering: (element: HTMLElement) => {
      window.requestAnimationFrame(() => { element.style.visibility = 'visible'; });
    },
  },
};

export const OnboardingRequirements = () => {
  const { draft, update, account, areas } = useOnboardingDraft();
  const { formatMessage: t } = useIntl();
  const classes = useStyles();
  const [formOpen, setFormOpen] = useState(false);
  const [description, setDescription] = useState(draft.requirement.description || '');
  const descriptionRef = useRef(draft.requirement.description || '');
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const item = draft.requirement;
  const patch = (change: Partial<typeof item>) => update(current => ({ ...current, requirement: { ...current.requirement, ...change } }));

  // Table data ko memoize kiya taaki form me type karne par MTable reset na ho
  const { requirementsById, existingRequirements, rows } = useMemo(() => {
    const byId = new Map<number, typeof account.otherRequirements>();
    (account.otherRequirements || []).forEach(requirement => {
      const list = byId.get(requirement.id) || [];
      list.push(requirement);
      byId.set(requirement.id, list);
    });
    const draftById = new Map(draft.requirements.filter(r => r.id).map(r => [r.id!, r]));
    const existing = Array.from(byId.values())
      .map(list => list[0])
      .filter(requirement => !(draft.requirementIdsToDelete || []).includes(requirement.id));
    const unitNames = new Map(draft.units.map(unit => [unit.id, unit.name]));
    const tableRows = [
      ...existing.map(requirement => {
        const edited = draftById.get(requirement.id);
        const units = edited
          ? edited.unitIds.map(id => unitNames.get(id)).filter(Boolean).join(', ')
          : Array.from(new Set((byId.get(requirement.id) || []).map(r => r.companyUnitName))).join(', ');
        return { id: requirement.id, name: edited?.name || requirement.name, units };
      }),
      ...draft.requirements.filter(r => !r.id || r.id < 0).map(requirement => ({
        id: requirement.id || -1,
        name: requirement.name,
        units: requirement.unitIds.map(id => unitNames.get(id)).filter(Boolean).join(', '),
      })),
    ];
    return { requirementsById: byId, existingRequirements: existing, rows: tableRows };
  }, [account.otherRequirements, draft.requirements, draft.requirementIdsToDelete, draft.units]);

  const edit = (id: number) => {
    // Table action callbacks may retain an older render; always open the latest saved draft.
    const draftRequirement = draftRef.current.requirements.find(requirement => requirement.id === id);
    if (draftRequirement) {
      descriptionRef.current = draftRequirement.description;
      setDescription(draftRequirement.description);
      update(current => ({ ...current, requirement: { ...draftRequirement, unitIds: [...draftRequirement.unitIds] } }));
    } else {
      const requirement = existingRequirements.find(r => r.id === id);
      if (!requirement) return;
      descriptionRef.current = requirement.description;
      setDescription(requirement.description);
      update(current => ({ ...current, requirement: {
        id: requirement.id, name: requirement.name, area: requirement.area,
        effectiveFrom: requirement.effectiveFrom.slice(0, 10), effectiveTo: requirement.effectiveTo?.slice(0, 10),
        description: requirement.description, unitIds: Array.from(new Set((requirementsById.get(id) || []).map(row => row.companyUnitId))),
      } }));
    }
    setFormOpen(true);
  };

  const save = () => {
    update(current => {
      const edited = { ...current.requirement, description: descriptionRef.current };
      const requirementNotes = { ...current.requirementNotes };
      const requirementNoteChanges = { ...current.requirementNoteChanges };
      current.requirement.unitIds.filter(unitId => !edited.unitIds.includes(unitId)).forEach(unitId => {
        const key = `${current.requirement.id}:${unitId}`;
        delete requirementNotes[key];
        delete requirementNoteChanges[key];
      });
      if (edited.id) {
        const exists = current.requirements.some(requirement => requirement.id === edited.id);
        return { ...current, requirements: exists
          ? current.requirements.map(requirement => requirement.id === edited.id ? edited : requirement)
          : [...current.requirements, edited], requirementNotes, requirementNoteChanges, requirement: emptyRequirement() };
      }
      const id = Math.min(0, ...current.requirements.map(requirement => requirement.id || 0)) - 1;
      return { ...current, requirements: [...current.requirements, { ...edited, id }], requirementNotes, requirementNoteChanges, requirement: emptyRequirement() };
    });
    setFormOpen(false);
  };

  const cancel = () => {
    descriptionRef.current = '';
    setDescription('');
    update(current => ({ ...current, requirement: emptyRequirement() }));
    setFormOpen(false);
  };

  const remove = (id: number) => {
    // Close the editor when its current requirement is deleted.
    if (draftRef.current.requirement.id === id) {
      descriptionRef.current = '';
      setDescription('');
      setFormOpen(false);
    }
    update(current => ({
      ...current,
      requirements: current.requirements.filter(requirement => requirement.id !== id),
      requirementIdsToDelete: id > 0 ? Array.from(new Set([...(current.requirementIdsToDelete || []), id])) : current.requirementIdsToDelete,
      requirementNotes: Object.fromEntries(Object.entries(current.requirementNotes).filter(([key]) => !key.startsWith(`${id}:`))),
      requirementNoteChanges: Object.fromEntries(Object.entries(current.requirementNoteChanges).filter(([key]) => !key.startsWith(`${id}:`))),
      requirement: current.requirement.id === id ? emptyRequirement() : current.requirement,
    }));
  };

  // Keep table actions stable while always calling the latest handlers.
  const editRef = useRef(edit);
  editRef.current = edit;
  const removeRef = useRef(remove);
  removeRef.current = remove;

  const columns = useMemo(() => [
    { title: t({ id: 'onboarding.requirement-name' }), field: 'name' },
    { title: t({ id: 'law-portal.overview-notes.form.company-units.label' }), field: 'units' },
  ], [t]);

  const actions = useMemo(() => [
    (row: { id: number }) => ({ icon: 'edit', tooltip: t({ id: 'component.mtable.editTooltip' }), onClick: () => editRef.current(row.id) }),
    (row: { id: number }) => ({ icon: 'delete', tooltip: t({ id: 'component.mtable.deleteTooltip' }), onClick: () => removeRef.current(row.id) }),
  ], [t]);

  const table = useMemo(() => <MTable data={rows} columns={columns} actions={actions} />, [rows, columns, actions]);

  const form = <Grid key={item.id || 'new-requirement'} container spacing={3} className={classes.adminFields}>
    <Grid item xs={12} sm={6}><OnboardingDraftText label={t({ id: 'form.name' })} value={item.name} onChange={name => patch({ name })} /></Grid>
    <Grid item xs={12} sm={6}><OnboardingDraftSelect name="requirement-area" label={t({ id: 'utils.filters.area' })} ids={item.area ? [item.area] : []} options={areas} multi={false} required onChange={ids => patch({ area: ids[0] || 0 })} /></Grid>
    <Grid item xs={12} sm={6}><DateField name="effectiveFrom" label={t({ id: 'law-portal.manage.requirements.effectiveFrom' })} required initialState={fromDateOnly(item.effectiveFrom)} errorTexts={[]} PopoverProps={datePickerPopoverProps} onChange={(value?: TypedFormFieldValue<Date>) => patch({ effectiveFrom: toDateOnly(value) || '' })} /></Grid>
    <Grid item xs={12} sm={6}><DateField name="effectiveTo" label={t({ id: 'law-portal.manage.requirements.effectiveTo' })} initialState={fromDateOnly(item.effectiveTo)} errorTexts={[]} PopoverProps={datePickerPopoverProps} onChange={(value?: TypedFormFieldValue<Date>) => patch({ effectiveTo: toDateOnly(value) })} /></Grid>
    <Grid item xs={12} sm={6}><OnboardingDraftSelect name="requirement-units" label={t({ id: 'form.selectCompanyUnits' })} ids={item.unitIds} options={draft.units} required onChange={unitIds => patch({ unitIds })} /></Grid>
    {/* patch({ description }) hata diya: save() descriptionRef.current use karta hai, isse har keystroke par re-render kam hota hai */}
    <Grid item xs={12}>
      <Typography variant="subtitle2">{t({ id: 'law-portal.manage.requirements.description' })}</Typography>
      <RichTextEditor value={description} onChange={value => { descriptionRef.current = value; setDescription(value); patch({ description: value }); }} overrideConfig={{ height: 180 }} />
    </Grid>
    <Grid item xs={12} className={classes.formActions}>
      <Button variant="outlined" color="primary" disabled={!validRequirement({ ...item, description })} onClick={save}>{t({ id: item.id ? 'form.save' : 'form.add' })}</Button>
      <Button style={{ marginLeft: 8 }} onClick={cancel}>{t({ id: 'form.cancel' })}</Button>
    </Grid>
  </Grid>;

  return <>
    <div className={classes.listActions}>
      {!formOpen && <Button color="primary" variant="outlined" onClick={() => {
        descriptionRef.current = '';
        setDescription('');
        update(current => ({ ...current, requirement: emptyRequirement() }));
        setFormOpen(true);
      }}>{t({ id: 'onboarding.add-requirement' })}</Button>}
    </div>
    {formOpen && form}
    {table}
  </>;
};
